/**
 * Shared upload acceptance rules for chat and question answers: which MIME types and
 * file extensions the general agent accepts, plus media-type classification
 * helpers shared by the composer's upload pipelines.
 */

export const ACCEPTED_UPLOAD_MIME_TYPES: Array<string> = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  'image/heic',
  'image/heif',
  'image/avif',
  'image/svg+xml',
  'application/pdf',
  'application/illustrator',
  'application/vnd.adobe.illustrator',
  'application/x-adobe-illustrator',
  'application/x-illustrator',
  'image/x-illustrator',
  'image/vnd.adobe.photoshop',
  'application/json',
  'application/vnd.openpencil+json',
  'application/vnd.excalidraw+json',
  'application/vnd.canvas-harness+json',
  'application/vnd.graphite+json',
  'application/zip',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroenabled.12',
  'application/vnd.ms-excel',
  'text/plain',
  'text/markdown',
  'text/csv',
  'video/mp4',
  'video/webm',
  'video/quicktime',
  'model/gltf-binary',
  'model/gltf+json',
  'model/stl',
  'application/sla',
  'application/vnd.ms-pki.stl',
];

export const ACCEPTED_UPLOAD_EXTENSIONS_TO_MIME: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.webp': 'image/webp',
  '.gif': 'image/gif',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.avif': 'image/avif',
  '.svg': 'image/svg+xml',
  '.pdf': 'application/pdf',
  '.ai': 'application/vnd.adobe.illustrator',
  '.psd': 'image/vnd.adobe.photoshop',
  '.json': 'application/json',
  '.op': 'application/vnd.openpencil+json',
  '.excalidraw': 'application/vnd.excalidraw+json',
  '.canvas': 'application/vnd.canvas-harness+json',
  '.graphite': 'application/vnd.graphite+json',
  '.zip': 'application/zip',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.ts': 'text/plain',
  '.csv': 'text/csv',
  '.ppt': 'application/vnd.ms-powerpoint',
  '.pptx':
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  '.doc': 'application/msword',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  '.xls': 'application/vnd.ms-excel',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  '.xlsm': 'application/vnd.ms-excel.sheet.macroenabled.12',
  '.mp4': 'video/mp4',
  '.webm': 'video/webm',
  '.mov': 'video/quicktime',
  '.glb': 'model/gltf-binary',
  '.gltf': 'model/gltf+json',
  '.stl': 'model/stl',
};

const PHOTOSHOP_UPLOAD_MIME_TYPES = new Set([
  'image/vnd.adobe.photoshop',
  'image/x-photoshop',
  'image/photoshop',
  'image/psd',
  'application/x-photoshop',
  'application/photoshop',
  'application/psd',
]);

/**
 * Layered Photoshop sources travel as files, not images: browsers cannot
 * decode them and the model cannot read them. Brand intake stores them and
 * reads the flattened preview it saves beside them.
 */
export function isPsdUpload(args: {
  filename: string;
  mimeType?: string | null;
}): boolean {
  const filename = args.filename.trim().toLowerCase();
  const mimeType = (args.mimeType ?? '').trim().toLowerCase();
  return filename.endsWith('.psd') || PHOTOSHOP_UPLOAD_MIME_TYPES.has(mimeType);
}

export const ACCEPTED_UPLOAD_ACCEPT_ATTRIBUTE = [
  ...ACCEPTED_UPLOAD_MIME_TYPES,
  // macOS reports Adobe Illustrator files to Chromium as PostScript. Keep
  // this picker-only MIME alongside `.ai`; runtime validation below still
  // rejects ordinary `.ps` files and only treats PostScript as Illustrator
  // when the filename ends in `.ai`.
  'application/postscript',
  ...Object.keys(ACCEPTED_UPLOAD_EXTENSIONS_TO_MIME),
].join(',');

const ACCEPTED_IMAGE_UPLOAD_MIME_TYPES = ACCEPTED_UPLOAD_MIME_TYPES.filter(
  (mimeType) =>
    mimeType.startsWith('image/') &&
    mimeType !== 'image/x-illustrator' &&
    !isPsdUpload({ filename: '', mimeType })
);
const ACCEPTED_IMAGE_UPLOAD_EXTENSIONS = Object.entries(
  ACCEPTED_UPLOAD_EXTENSIONS_TO_MIME
).flatMap(([extension, mimeType]) =>
  mimeType.startsWith('image/') && !isPsdUpload({ filename: extension })
    ? [extension]
    : []
);

export const ACCEPTED_IMAGE_UPLOAD_ACCEPT_ATTRIBUTE = [
  ...ACCEPTED_IMAGE_UPLOAD_MIME_TYPES,
  ...ACCEPTED_IMAGE_UPLOAD_EXTENSIONS,
].join(',');

export function getAcceptedUploadExtension(filename: string): string | null {
  const lower = filename.toLowerCase();
  return (
    Object.keys(ACCEPTED_UPLOAD_EXTENSIONS_TO_MIME).find((extension) =>
      lower.endsWith(extension)
    ) ?? null
  );
}

export function getGeneralAgentUploadMimeType(file: {
  name: string;
  type: string;
}): string {
  const mimeType = file.type.trim().toLowerCase();
  if (isIllustratorUpload({ filename: file.name, mimeType })) {
    return 'application/vnd.adobe.illustrator';
  }
  if (isPsdUpload({ filename: file.name, mimeType })) {
    return 'image/vnd.adobe.photoshop';
  }
  if (mimeType.length > 0 && mimeType !== 'application/octet-stream') {
    return mimeType;
  }
  const extension = getAcceptedUploadExtension(file.name);
  if (extension) {
    return (
      ACCEPTED_UPLOAD_EXTENSIONS_TO_MIME[extension] ??
      (mimeType || 'application/octet-stream')
    );
  }
  return mimeType || 'application/octet-stream';
}

export function isAcceptedUploadMimeType(mimeType: string): boolean {
  const normalized = mimeType.trim().toLowerCase();
  if (!normalized) return false;
  return ACCEPTED_UPLOAD_MIME_TYPES.includes(normalized);
}

/**
 * Drag-over acceptance. A dragged item exposes only a MIME type, never a
 * filename, and macOS reports Illustrator files as `application/postscript`,
 * so that type must pass here or `.ai` files can never be dropped. The
 * drop-time `isAcceptedUploadFile` check still rejects plain `.ps` uploads
 * because their filename does not end in `.ai`.
 */
export function isAcceptedDragOverMimeType(mimeType: string): boolean {
  const normalized = mimeType.trim().toLowerCase();
  return (
    normalized === 'application/postscript' ||
    isAcceptedUploadMimeType(normalized)
  );
}

export function isStlUploadMimeType(mimeType: string): boolean {
  const normalized = mimeType.trim().toLowerCase();
  return (
    normalized === 'model/stl' ||
    normalized === 'application/sla' ||
    normalized === 'application/vnd.ms-pki.stl'
  );
}

export function isIllustratorUpload(args: {
  filename: string;
  mimeType?: string | null;
}): boolean {
  const filenameIsAi = args.filename.trim().toLowerCase().endsWith('.ai');
  const mimeType = (args.mimeType ?? '').trim().toLowerCase();
  if (mimeType === 'application/postscript') return filenameIsAi;
  return (
    filenameIsAi ||
    mimeType === 'application/illustrator' ||
    mimeType === 'application/vnd.adobe.illustrator' ||
    mimeType === 'application/x-adobe-illustrator' ||
    mimeType === 'application/x-illustrator' ||
    mimeType === 'image/x-illustrator'
  );
}

export function isAcceptedUploadFile(file: {
  name: string;
  type: string;
}): boolean {
  const rawMimeType = file.type.trim().toLowerCase();
  if (rawMimeType === 'application/postscript') {
    return file.name.trim().toLowerCase().endsWith('.ai');
  }
  if (isPsdUpload({ filename: file.name, mimeType: rawMimeType })) return true;
  if (rawMimeType && rawMimeType !== 'application/octet-stream') {
    return isAcceptedUploadMimeType(rawMimeType);
  }
  return getAcceptedUploadExtension(file.name) !== null;
}

export function isAcceptedImageUploadFile(file: {
  name: string;
  type: string;
}): boolean {
  return isAcceptedUploadFile(file) && getUploadMediaType(file) === 'image';
}

export function getUploadMediaType(file: {
  name: string;
  type: string;
}): 'image' | 'video' | 'audio' | 'file' | 'three-d' {
  const mimeType = getGeneralAgentUploadMimeType(file);
  const lowerName = file.name.toLowerCase();
  if (isIllustratorUpload({ filename: file.name, mimeType })) return 'file';
  if (isPsdUpload({ filename: file.name, mimeType })) return 'file';
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (
    mimeType.startsWith('model/') ||
    isStlUploadMimeType(mimeType) ||
    lowerName.endsWith('.glb') ||
    lowerName.endsWith('.gltf') ||
    lowerName.endsWith('.stl')
  ) {
    return 'three-d';
  }
  return 'file';
}
